import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, ChevronRight, Check, Loader2, UserPlus, Trash2, Unlock } from "lucide-react";
import WeaponSummary from "@/components/WeaponSummary";
import { useAuth } from "@/context/AuthContext";
import { AthleteSelectDialog } from "@/components/AthleteSelect";
import { displayName, compareAthletes } from "@/lib/athleteName";

const STATUS_LABEL = {
  presente: "Presente", assente: "Assente", giustificato: "Giustificato",
  ritardo: "Ritardo", parziale: "Parziale", malato: "Malato",
  infortunio: "Infortunio", sospeso: "Sospeso", altro: "Altro",
};

/**
 * TrainingPicker (legacy — used by Dashboard CTA when no training rows are visible).
 * No auto-open: coach must voluntarily pick.
 */
export default function AttendanceQuickPickerDialog({ open, onOpenChange, onOpenRegister }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/attendance/today");
      setItems(data || []);
    } catch { setItems([]); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const openTraining = async (t) => {
    setCreating(true);
    try {
      let regId = t.register?.id;
      if (!regId) {
        const { data } = await api.post("/attendance", { training_id: t.training_id });
        regId = data.id;
      }
      onOpenChange(false);
      onOpenRegister(regId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="attendance-picker">
        <DialogHeader>
          <DialogTitle>Scegli l'allenamento di oggi</DialogTitle>
          <DialogDescription>
            {items === null ? "Rilevamento delle attività previste…" :
             items.length === 0 ? "Nessun allenamento pianificato per oggi." :
             "Tocca il turno per aprire il registro presenze."}
          </DialogDescription>
        </DialogHeader>
        {items === null && <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>}
        {items !== null && items.length === 0 && (
          <div className="text-sm text-zinc-500 py-4">
            Nessuna attività da registrare. Crea un allenamento in <a className="underline" href="/allenamento">Allenamento</a>.
          </div>
        )}
        {items && items.length > 0 && (
          <div className="divide-y divide-zinc-100 -mx-6 max-h-96 overflow-y-auto">
            {items.map((t) => (
              <button
                key={t.training_id}
                data-testid={`picker-training-${t.training_id}`}
                onClick={() => openTraining(t)}
                disabled={creating}
                className="w-full flex items-center gap-3 px-6 py-3 hover:bg-zinc-50 text-left"
              >
                <Clock className="w-4 h-4 text-zinc-400" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.title || t.group_name || "Allenamento"}</div>
                  <div className="text-xs text-zinc-500 font-mono">{t.date} · {t.duration || 90}′ · {t.coach_name || "—"}</div>
                  <div className="mt-1"><WeaponSummary weapons={(t.weapons || []).map((w) => ({ weapon_code: w, is_primary: true, status: "attiva" }))} /></div>
                </div>
                <div className="text-xs text-zinc-500">{t.expected_count} attesi</div>
                <StatusPill status={t.status} />
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function StatusPill({ status }) {
  const map = {
    da_registrare: { l: "Da registrare", c: "bg-amber-100 text-amber-800" },
    bozza: { l: "In bozza", c: "bg-blue-100 text-blue-800" },
    completato: { l: "Completato", c: "bg-emerald-100 text-emerald-800" },
  };
  const s = map[status] || { l: status, c: "bg-zinc-100 text-zinc-600" };
  return <span data-testid={`attendance-status-${status}`} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${s.c}`}>{s.l}</span>;
}

const PRIMARY_STATUSES = ["presente", "assente"];
const SECONDARY_STATUSES = ["giustificato", "ritardo", "parziale", "malato", "infortunio", "sospeso", "altro"];

export function AttendanceRegisterEditor({ registerId, siblings, onNavigate, onClose, onSaved }) {
  const { user } = useAuth();
  const [reg, setReg] = useState(null);
  const [names, setNames] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [allAthletes, setAllAthletes] = useState([]);
  const [groups, setGroups] = useState([]);
  const dirtyRef = useRef(false);
  const canReopen = user && ["admin", "coach", "trainer"].includes(user.role);

  const load = useCallback(async () => {
    setReg(null);
    dirtyRef.current = false;
    const r = await api.get(`/attendance/${registerId}`);
    setReg(r.data);
    setNames(r.data.athlete_names || {});  // solo i nomi degli atleti del registro (no roster completo)
  }, [registerId]);

  useEffect(() => { if (registerId) load(); }, [registerId, load]);
  useEffect(() => { api.get("/users").then((r) => setStaff((r.data || []).filter((u) => ["coach", "admin", "trainer"].includes(u.role)))).catch(() => {}); }, []);

  // Roster + gruppi caricati SOLO quando serve aggiungere un atleta non previsto (caso eccezionale)
  const ensureRoster = useCallback(async () => {
    if (allAthletes.length === 0) {
      const [a, g] = await Promise.all([api.get("/athletes"), api.get("/groups").catch(() => ({ data: [] }))]);
      setAllAthletes(a.data || []);
      setGroups(g.data || []);
    }
    setAddOpen(true);
  }, [allAthletes.length]);

  const toggleCoach = (cid) => {
    dirtyRef.current = true;
    setReg((r) => {
      const has = (r.coach_ids || []).includes(cid);
      return { ...r, coach_ids: has ? r.coach_ids.filter((x) => x !== cid) : [...(r.coach_ids || []), cid] };
    });
  };
  const setEffMin = (v) => { dirtyRef.current = true; setReg((r) => ({ ...r, effective_minutes: v })); };

  const updateEntry = (aid, patch) => {
    dirtyRef.current = true;
    setReg((r) => ({ ...r, entries: r.entries.map((e) => e.athlete_id === aid ? { ...e, ...patch } : e) }));
  };

  const bulkSet = (status) => {
    dirtyRef.current = true;
    setReg((r) => ({ ...r, entries: r.entries.map((e) => ({ ...e, status })) }));
  };

  const addAthlete = (aid) => {
    if (!aid || (reg?.entries || []).some((e) => e.athlete_id === aid)) { setAddOpen(false); return; }
    dirtyRef.current = true;
    const a = allAthletes.find((x) => x.id === aid);
    if (a) setNames((n) => ({ ...n, [aid]: { first_name: a.first_name || "", last_name: a.last_name || "", weapon: a.weapon || "", category: a.category || "" } }));
    setReg((r) => ({ ...r, entries: [...r.entries, { athlete_id: aid, status: "presente" }] }));
    setAddOpen(false);
    toast.success("Atleta aggiunto — solo per questa seduta");
  };

  const removeManualAthlete = (aid) => {
    dirtyRef.current = true;
    setReg((r) => ({ ...r, entries: r.entries.filter((e) => e.athlete_id !== aid) }));
  };

  const isManualEntry = (aid) => {
    if (!reg) return false;
    return !(reg.expected_athlete_ids || []).includes(aid);
  };

  const save = async (finalize) => {
    setSaving(true);
    try {
      const body = { entries: reg.entries, status: finalize ? "completato" : "bozza", notes: reg.notes, coach_ids: reg.coach_ids || [], effective_minutes: reg.effective_minutes ?? null };
      const { data } = await api.put(`/attendance/${registerId}`, body);
      toast.success(finalize ? "Presenze completate" : "Bozza salvata");
      dirtyRef.current = false;
      if (data) { setReg(data); setNames(data.athlete_names || names); }
      onSaved?.();
      if (finalize) onClose?.();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio · modifiche mantenute");
      return false;
    } finally { setSaving(false); }
  };

  const reopenRegister = async () => {
    if (!window.confirm("Riaprire questo registro per modifica?\nLo stato tornerà a \"In bozza\" e le modifiche saranno tracciate nell'audit.")) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/attendance/${registerId}`, { status: "bozza" });
      setReg(data); setNames(data.athlete_names || names);
      dirtyRef.current = false;
      toast.success("Registro riaperto per modifica");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const handleSwitch = async (sib) => {
    if (!sib || sib.register_id === registerId || sib.training_id === reg?.training_id) return;
    if (dirtyRef.current) {
      const ok = await save(false);
      if (!ok) return;
    }
    onNavigate?.({ training_id: sib.training_id, register: sib.register_id ? { id: sib.register_id } : null });
  };

  const stats = useMemo(() => {
    if (!reg) return { presente: 0, assente: 0, altri: 0, totale: 0 };
    const s = { presente: 0, assente: 0, altri: 0, totale: reg.entries.length };
    reg.entries.forEach((e) => {
      if (e.status === "presente") s.presente++;
      else if (e.status === "assente") s.assente++;
      else s.altri++;
    });
    return s;
  }, [reg]);

  const isCompleted = reg?.status === "completato";
  const titleDate = reg?.date ? new Date(reg.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }) : "";
  const currentTitle = useMemo(() => {
    if (!reg) return "";
    const s = (siblings || []).find((x) => x.register_id === reg.id || x.training_id === reg.training_id);
    return s?.title || reg.activity || "Presenze";
  }, [reg, siblings]);

  const nameOf = (aid) => {
    const n = names[aid];
    if (!n) return aid.slice(0, 8);
    return displayName(n) === "—" ? aid.slice(0, 8) : displayName(n);
  };

  return (
    <Dialog open={!!registerId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl p-0 max-h-[94vh] flex flex-col overflow-hidden"
        data-testid="attendance-editor"
      >
        {/* Sticky header */}
        <div className="px-5 pt-5 pb-3 border-b border-zinc-100 shrink-0">
          <DialogHeader className="text-left space-y-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <DialogTitle className="text-lg">{currentTitle}</DialogTitle>
              <span className="text-xs text-zinc-500 capitalize">· {titleDate}</span>
            </div>
            <DialogDescription>
              {isCompleted
                ? "Presenze completate · sola lettura."
                : "Tutti presenti di default. Tocca solo le eccezioni."}
            </DialogDescription>
          </DialogHeader>

          {/* Sibling switcher */}
          {siblings && siblings.length > 1 && (
            <div className="mt-3 flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="sibling-switcher">
              {siblings.map((s) => {
                const isCurrent = s.register_id === reg?.id || (reg?.training_id && s.training_id === reg.training_id);
                return (
                  <button
                    key={s.training_id}
                    onClick={() => handleSwitch(s)}
                    disabled={isCurrent || saving}
                    data-testid={`sibling-${s.training_id}`}
                    className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full border transition ${
                      isCurrent
                        ? "bg-black text-white border-black"
                        : s.status === "completato"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400"
                          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
                    }`}
                  >
                    {s.time && <span className="font-mono mr-1 opacity-70">{s.time}</span>}
                    {s.title}
                    {s.status === "completato" && !isCurrent && <Check className="w-3 h-3 inline ml-1" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Live counter — Presenti X/Y */}
          {reg && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="attendance-live-counter">
              <span data-testid="counter-presenti" className="inline-flex items-baseline gap-1.5 text-sm px-3 py-1.5 rounded-md border bg-emerald-50 text-emerald-800 border-emerald-200">
                <span className="uppercase tracking-wider text-[11px]">Presenti</span>
                <strong className="font-mono text-base">{stats.presente}/{stats.totale}</strong>
              </span>
              {stats.assente > 0 && (
                <span data-testid="counter-assenti" className="inline-flex items-baseline gap-1.5 text-sm px-3 py-1.5 rounded-md border bg-red-50 text-red-800 border-red-200">
                  <span className="uppercase tracking-wider text-[11px]">Assenti</span>
                  <strong className="font-mono text-base">{stats.assente}</strong>
                </span>
              )}
              {stats.altri > 0 && (
                <span data-testid="counter-altri" className="inline-flex items-baseline gap-1.5 text-sm px-3 py-1.5 rounded-md border bg-zinc-50 text-zinc-700 border-zinc-200">
                  <span className="uppercase tracking-wider text-[11px]">Altri</span>
                  <strong className="font-mono text-base">{stats.altri}</strong>
                </span>
              )}
              {(reg.weapons || []).length > 0 && (
                <div className="w-full mt-1"><WeaponSummary weapons={reg.weapons.map((w) => ({ weapon_code: w, is_primary: true, status: "attiva" }))} /></div>
              )}
            </div>
          )}
        </div>

        {/* Scroll area */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!reg ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
          ) : (
            <div className="space-y-4">
              {!isCompleted && reg.entries.length > 1 && (
                <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
                  <span>Azione rapida:</span>
                  <button onClick={() => bulkSet("presente")} className="px-2.5 py-1.5 rounded border border-zinc-200 hover:border-zinc-400" data-testid="bulk-all-present">Tutti presenti</button>
                  <button onClick={() => bulkSet("assente")} className="px-2.5 py-1.5 rounded border border-zinc-200 hover:border-zinc-400" data-testid="bulk-all-absent">Tutti assenti</button>
                </div>
              )}

              {/* Lista atleti — interazione primaria binaria */}
              <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-md">
                {reg.entries.length === 0 && <div className="p-6 text-sm text-zinc-500">Nessun atleta previsto. Usa "Aggiungi atleta" per inserirne uno.</div>}
                {[...reg.entries].sort((x, y) => compareAthletes(names[x.athlete_id], names[y.athlete_id])).map((e) => {
                  const manual = isManualEntry(e.athlete_id);
                  const isSecondary = SECONDARY_STATUSES.includes(e.status);
                  const isOpen = expanded[e.athlete_id] || isSecondary;
                  return (
                    <div key={e.athlete_id} data-testid={`att-entry-${e.athlete_id}`} className={`px-4 py-3 ${manual ? "bg-amber-50/40" : ""}`}>
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{nameOf(e.athlete_id)}</div>
                          {manual && <div className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold" data-testid={`manual-badge-${e.athlete_id}`}>Aggiunto manualmente</div>}
                          {isSecondary && <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{STATUS_LABEL[e.status]}</div>}
                        </div>

                        {/* Toggle binario primario */}
                        {!isCompleted ? (
                          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden shrink-0" data-testid={`att-toggle-${e.athlete_id}`}>
                            <button
                              data-testid={`att-${e.athlete_id}-presente`}
                              onClick={() => updateEntry(e.athlete_id, { status: "presente" })}
                              className={`px-3.5 h-10 text-xs uppercase tracking-wider transition ${e.status === "presente" ? "bg-emerald-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                            >Presente</button>
                            <button
                              data-testid={`att-${e.athlete_id}-assente`}
                              onClick={() => updateEntry(e.athlete_id, { status: "assente" })}
                              className={`px-3.5 h-10 text-xs uppercase tracking-wider border-l border-zinc-300 transition ${e.status === "assente" ? "bg-red-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                            >Assente</button>
                            <button
                              data-testid={`att-more-${e.athlete_id}`}
                              onClick={() => setExpanded((m) => ({ ...m, [e.athlete_id]: !m[e.athlete_id] }))}
                              className={`px-3 h-10 text-sm border-l border-zinc-300 transition ${isOpen && !isSecondary ? "bg-zinc-100" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
                              aria-label="Altre opzioni"
                            >…</button>
                          </div>
                        ) : (
                          <StatusPill status={e.status === "presente" ? "completato" : e.status} />
                        )}

                        {manual && !isCompleted && (
                          <button onClick={() => removeManualAthlete(e.athlete_id)} className="p-1.5 rounded hover:bg-amber-100 text-amber-700 shrink-0" title="Rimuovi (solo per questa seduta)" data-testid={`remove-manual-${e.athlete_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* Stati meno frequenti + nota (dietro …) */}
                      {isOpen && !isCompleted && (
                        <div className="mt-2 pl-1 space-y-2" data-testid={`att-more-panel-${e.athlete_id}`}>
                          <div className="flex flex-wrap gap-1">
                            {SECONDARY_STATUSES.map((s) => (
                              <button
                                key={s}
                                data-testid={`att-${e.athlete_id}-${s}`}
                                onClick={() => updateEntry(e.athlete_id, { status: s })}
                                className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition ${e.status === s ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
                              >{STATUS_LABEL[s]}</button>
                            ))}
                          </div>
                          {e.status !== "presente" && (
                            <Input
                              placeholder="Nota (facoltativa)"
                              value={e.notes || ""}
                              onChange={(ev) => updateEntry(e.athlete_id, { notes: ev.target.value })}
                              className="h-9 text-xs"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Metadati secondari e collassabili: tecnici, durata, aggiungi atleta, audit */}
              <details className="group border border-zinc-200 rounded-md" data-testid="session-meta">
                <summary className="cursor-pointer px-4 py-2.5 text-sm text-zinc-600 flex items-center gap-2 select-none">
                  <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  Dettagli seduta · tecnici, durata
                </summary>
                <div className="px-4 pb-4 pt-1 space-y-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Tecnici presenti</div>
                    <div className="flex flex-wrap gap-1.5">
                      {staff.length === 0 && <span className="text-xs text-zinc-400">Nessun tecnico disponibile</span>}
                      {staff.map((u) => {
                        const on = (reg.coach_ids || []).includes(u.id);
                        return (
                          <button key={u.id} type="button" disabled={isCompleted} data-testid={`coach-toggle-${u.id}`} onClick={() => toggleCoach(u.id)}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${on ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"} ${isCompleted ? "opacity-70 cursor-not-allowed" : ""}`}>
                            {u.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">Durata effettiva</div>
                    <Input type="number" min={0} disabled={isCompleted} data-testid="effective-minutes"
                      value={reg.effective_minutes ?? ""} onChange={(e) => setEffMin(e.target.value === "" ? null : parseInt(e.target.value))}
                      className="h-8 w-24 text-sm" placeholder="min" />
                    <span className="text-xs text-zinc-500">min</span>
                  </div>
                  {!isCompleted && (
                    <button onClick={ensureRoster} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-dashed border-zinc-300 hover:border-zinc-500 text-zinc-600 text-xs" data-testid="btn-add-athlete">
                      <UserPlus className="w-3.5 h-3.5" /> Aggiungi atleta (caso eccezionale)
                    </button>
                  )}
                  {reg.audit && reg.audit.length > 0 && (
                    <details className="text-xs text-zinc-500">
                      <summary className="cursor-pointer">Storico modifiche ({reg.audit.length})</summary>
                      <ul className="mt-2 space-y-1">
                        {reg.audit.slice(-10).reverse().map((a, i) => (
                          <li key={i}><span className="font-mono">{a.at?.slice(0, 16)}</span> · {a.by} · {a.change}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </details>

              <AthleteSelectDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                athletes={allAthletes}
                groups={groups}
                excludeIds={reg.entries.map((e) => e.athlete_id)}
                title="Aggiungi atleta alla seduta"
                onPick={addAthlete}
                testid="add-athlete"
              />
            </div>
          )}
        </div>

        {/* Sticky footer — una sola CTA primaria */}
        {reg && (
          <div className="px-5 py-3 border-t border-zinc-100 bg-white flex items-center justify-between gap-3 shrink-0">
            <div className="text-xs text-zinc-500">
              {isCompleted ? (
                <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" /> Presenze completate</span>
              ) : (
                <button onClick={() => save(false)} disabled={saving} data-testid="attendance-save-draft" className="underline hover:text-zinc-800">Salva bozza</button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} data-testid="attendance-close">Chiudi</Button>
              {isCompleted && canReopen && (
                <Button variant="outline" onClick={reopenRegister} disabled={saving} data-testid="attendance-reopen" className="border-amber-300 text-amber-800 hover:bg-amber-50">
                  <Unlock className="w-4 h-4 mr-2" />Riapri per modifica
                </Button>
              )}
              {!isCompleted && (
                <Button className="bg-black hover:bg-zinc-800 h-11 px-5" onClick={() => save(true)} disabled={saving} data-testid="attendance-complete">
                  <Check className="w-4 h-4 mr-2" />Completa presenze
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

